// Message dictionaries. Add new keys here and translate alongside.
// Only the active locale is loaded into memory at any time.
//
// We deliberately avoid next-intl / next-i18next for the bootstrap:
// they require restructuring the app-router (`/[locale]/...`) and
// pull in 200+kb of formatter machinery. Plain dictionaries plus the
// hook in `useTranslation.ts` cover 95% of what we actually need
// (strings + Intl.NumberFormat for currency).

export type Locale = 'en' | 'ar';

export const SUPPORTED_LOCALES: Array<{ code: Locale; label: string; rtl: boolean }> = [
  { code: 'en', label: 'English',  rtl: false },
  { code: 'ar', label: 'العربية', rtl: true  },
];

type Messages = Record<string, string>;

export const messages: Record<Locale, Messages> = {
  en: {
    'common.loading':            'Loading...',
    'common.save':               'Save',
    'common.cancel':             'Cancel',
    'common.delete':             'Delete',
    'common.confirm':            'Confirm',
    'common.search':             'Search',
    'common.error':              'Something went wrong',
    'common.success':            'Saved',
    'common.upgrade':            'Upgrade',
    'common.tryAgain':           'Try again',

    'nav.dashboard':             'Dashboard',
    'nav.unibox':                'Unibox',
    'nav.inboxes':               'Email Accounts',
    'nav.campaigns':             'Campaigns',
    'nav.leads':                 'Leads',
    'nav.prospecting':           'Prospecting',
    'nav.ibrain':                'iBrain',
    'nav.autoReply':             'Auto-Reply AI',
    'nav.templates':             'Templates',
    'nav.deliverability':        'Deliverability',
    'nav.analytics':             'Analytics',
    'nav.billing':               'Billing',
    'nav.developers':            'Developers',
    'nav.workspaces':            'Workspaces',
    'nav.infrastructure':        'Infrastructure',
    'nav.audit':                 'Audit log',
    'nav.settings':              'Settings',

    'billing.title':             'Billing',
    'billing.subtitle':          'Manage your subscription and payments. Powered by Ziina.',
    'billing.currentPlan':       'Current plan',
    'billing.noPlan':            'No plan',
    'billing.trial':             'Trial',
    'billing.daysLeft':          '{{days}} days left',
    'billing.cancel':            'Cancel',
    'billing.resume':            'Resume',
    'billing.subscribe':         'Subscribe',
    'billing.plans':             'Plans',
    'billing.history':           'Payment history',
    'billing.noHistory':         'No payments yet. Subscribe to a plan above to get started.',
    'billing.providerNotConfigured':
      'Payments are not configured on this server. Set ZIINA_API_KEY in the backend environment to enable checkout.',
  },

  ar: {
    'common.loading':            'جارٍ التحميل...',
    'common.save':               'حفظ',
    'common.cancel':             'إلغاء',
    'common.delete':             'حذف',
    'common.confirm':            'تأكيد',
    'common.search':             'بحث',
    'common.error':              'حدث خطأ ما',
    'common.success':            'تم الحفظ',
    'common.upgrade':            'ترقية',
    'common.tryAgain':           'حاول مرة أخرى',

    'nav.dashboard':             'لوحة التحكم',
    'nav.unibox':                'صندوق موحّد',
    'nav.inboxes':               'حسابات البريد',
    'nav.campaigns':             'الحملات',
    'nav.leads':                 'العملاء المحتملون',
    'nav.prospecting':           'البحث عن العملاء',
    'nav.ibrain':                'العقل الذكي',
    'nav.autoReply':             'الرد الآلي بالذكاء',
    'nav.templates':             'القوالب',
    'nav.deliverability':        'إيصال الرسائل',
    'nav.analytics':             'التحليلات',
    'nav.billing':               'الفوترة',
    'nav.developers':            'المطوّرون',
    'nav.workspaces':            'مساحات العمل',
    'nav.infrastructure':        'البنية التحتية',
    'nav.audit':                 'سجل التدقيق',
    'nav.settings':              'الإعدادات',

    'billing.title':             'الفوترة',
    'billing.subtitle':          'أدر اشتراكك ومدفوعاتك. مدعوم بـ Ziina.',
    'billing.currentPlan':       'الخطة الحالية',
    'billing.noPlan':            'لا توجد خطة',
    'billing.trial':             'تجربة',
    'billing.daysLeft':          'متبقّي {{days}} يومًا',
    'billing.cancel':            'إلغاء',
    'billing.resume':            'استئناف',
    'billing.subscribe':         'اشترك',
    'billing.plans':             'الخطط',
    'billing.history':           'سجل المدفوعات',
    'billing.noHistory':         'لا توجد مدفوعات بعد. اشترك بخطة أعلاه للبدء.',
    'billing.providerNotConfigured':
      'لم يتم إعداد المدفوعات على هذا الخادم. اضبط ZIINA_API_KEY في بيئة الخادم لتفعيل الدفع.',
  },
};

/** Format a translation, replacing {{name}} placeholders. */
export const formatMessage = (
  template: string,
  values?: Record<string, string | number>
): string => {
  if (!values) return template;
  return template.replace(/{{\s*(\w+)\s*}}/g, (_, key) =>
    values[key] != null ? String(values[key]) : `{{${key}}}`
  );
};
